import _ from 'lodash';
import React, { Component, forwardRef, createRef } from 'react';
import PropTypes from 'prop-types';
import { Scrollbars } from 'react-custom-scrollbars-2';
import OutsideClickHandler from 'react-outside-click-handler/esm/OutsideClickHandler';
import cn from 'classnames';
import { useTranslation, withTranslation } from 'react-i18next';

import { CheckBoxIcon, EmptyCheckBoxIcon, ExpandIcon } from 'components/common/icons';
import CustomTooltip from 'components/common/CustomTooltip';

import styles from 'components/common/Form/Select/Select.module.scss';
import Input from '../Input';

const translationFields = ['type', 'questionType', 'format', 'matrixChoiseType', 'matrixType', 'matrixContentType'];

const SelectOption = forwardRef(
    ({ value, selected, onClick, name, multiple, isWideValues, optionExtraClassName }, ref) => {
        const optionClassName = cn(styles.option, optionExtraClassName, {
            [styles.optionSelected]: selected,
            [styles.wideValues]: isWideValues,
        });
        const { t } = useTranslation();

        const label = _.isObject(value) ? value.label : value;
        return (
            <div
                className={optionClassName}
                onClick={() => onClick(_.isObject(value) ? value.value : value)}
                ref={ref}
                id={_.isObject(value) ? value.value : value}
            >
                {selected ? (
                    <div className={styles.checkIcon}>
                        <CheckBoxIcon />
                    </div>
                ) : (
                    multiple && (
                        <div className={styles.checkIcon}>
                            <EmptyCheckBoxIcon />
                        </div>
                    )
                )}
                {translationFields.includes(name) ? t(label) : label}
            </div>
        );
    },
);

class Select extends Component {
    static propTypes = {
        className: PropTypes.string,
        previewClassName: PropTypes.string,
        previewButtonFill: PropTypes.string,
        options: PropTypes.array,
        fullWidth: PropTypes.bool,
        readOnly: PropTypes.bool,
        name: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.any,
        placeholder: PropTypes.string,
        error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        multiple: PropTypes.bool,
        onChange: PropTypes.func,
        onChangeField: PropTypes.func,
        enabled: PropTypes.bool,
        withoutSearch: PropTypes.bool,
        errorClassName: PropTypes.string,
        labelClassName: PropTypes.string,
        onBlur: PropTypes.func,
        inputRootClassName: PropTypes.string,
        isWideOptionValues: PropTypes.bool,
        errorAbsolutePosition: PropTypes.bool,
    };

    static defaultProps = {
        enabled: true,
        optionsListHeight: '10rem',
        onOpen: _.noop,
        onBlur: _.noop,
    };

    constructor(props) {
        super(props);

        this.state = {
            isOpen: false,
            searchInputValue: '',
            tooltipHovered: false,
            isChanged: false,
        };

        this.searchInputRef = createRef();
        this.optionsRefs = {};
        this.scrollbarsRef = createRef();
    }

    componentDidMount() {
        this.initRefs();
    }

    // eslint-disable-next-line react/no-deprecated
    componentWillReceiveProps(nextProps) {
        if (!nextProps.enabled && this.props.enabled) {
            this.onChangeValue(null);
        }
    }

    handleClickOutside = () => {
        if (this.state.isOpen) {
            this.closeList();
        }
    };

    closeList = () => {
        this.setState({ isOpen: false, searchInputValue: '' });
    };

    toggleList = () => {
        const { readOnly } = this.props;

        if (readOnly) {
            return;
        }

        this.setState((state) => ({ isOpen: !state.isOpen }), this.onSelectOpen);
    };

    onSelectOpen = () => {
        this.searchInputRef.current && this.searchInputRef.current.focus();
        const { value, options, onOpen } = this.props;
        const { scrollbarsRef } = this;

        if (this.props.value) {
            const objectValue = _.isObject(value);
            const objectOptions = _.isObject(options[0]);
            const optionToScroll = this.props.options.find((option) => {
                if (objectOptions) {
                    return objectValue
                        ? value.value === option.value
                        : value === option.value || value === option.label;
                }

                return value === option;
            });

            if (!optionToScroll) return;

            const keyToScroll = Object.keys(this.optionsRefs).find((key) => {
                return objectOptions
                    ? key === String(optionToScroll.value) || key === optionToScroll.label
                    : optionToScroll === key;
            });

            const refToScroll = this.optionsRefs[keyToScroll];

            refToScroll &&
                refToScroll.current &&
                scrollbarsRef.current &&
                scrollbarsRef.current.scrollTop(refToScroll.current.offsetTop);
        }
        onOpen();
    };

    onOptionClick = (value) => {
        const { multiple, onBlur } = this.props;
        this.setState({ isChanged: true });
        if (multiple) {
            this.onSelectMultipleOption(value);
        } else {
            this.onChangeValue(value);
            this.closeList();
        }
        onBlur();
    };

    onSelectMultipleOption = (item) => {
        const { value: list } = this.props;

        const nextList = list.includes(item) ? _.without(list, item) : [...list, item];

        this.onChangeValue(nextList);
    };

    onChangeValue = (value) => {
        const { name, onChangeField, onChange } = this.props;
        if (onChangeField) {
            onChangeField({ target: { name, value } });
        } else {
            onChange && onChange(value);
        }
    };

    initRefs = () => {
        this.props.options.forEach((option) => {
            const key = _.isObject(option) ? option.value : option;
            this.optionsRefs[key] = createRef();
        });
    };

    handleSearchInput = (event) => {
        this.setState({ searchInputValue: event.target.value });
    };

    filterOptions = (option) => {
        const { t } = this.props;
        const stringToTest = _.isObject(option) ? option.label : option;
        const translateOrNot = translationFields.includes(this.props.name) ? t(stringToTest) : stringToTest;
        // eslint-disable-next-line no-useless-escape
        const regex = new RegExp(`${this.state.searchInputValue}`, 'ig');
        return regex.test(translateOrNot);
    };

    renderOptions = (options) => {
        if (!options.length) return <div className={styles.option}>No matches</div>;

        const { multiple, value, name, isWideOptionValues, optionExtraClassName } = this.props;
        return options.map((option) => {
            const selected = multiple && value.includes(option.value);
            const key = _.isObject(option) ? option.value : option;

            if (option.render) {
                return option.render;
            }

            return (
                <SelectOption
                    key={key}
                    value={option}
                    selected={selected}
                    multiple={multiple}
                    onClick={this.onOptionClick}
                    ref={this.optionsRefs[key]}
                    name={name}
                    isWideValues={isWideOptionValues}
                    optionExtraClassName={optionExtraClassName}
                />
            );
        });
    };

    renderPreview = () => {
        const { isOpen, searchInputValue } = this.state;
        const {
            readOnly,
            placeholder,
            withoutSearch,
            previewTextClassName,
            required,
            name,
            inputRootClassName,
            register,
        } = this.props;

        let inputProps = {
            value: searchInputValue,
        };

        if (register) {
            delete inputProps.value;

            inputProps = { ...inputProps, ...register(name, { required: required }) };
        }

        if (isOpen && !withoutSearch) {
            return (
                <input
                    {...inputProps}
                    ref={this.searchInputRef}
                    onChange={this.handleSearchInput}
                    className={cn(styles.searchInput, inputRootClassName)}
                    autoComplete='off'
                />
            );
        }

        return (
            <div
                className={cn(styles.previewText, {
                    [previewTextClassName]: previewTextClassName,
                    [styles.disabled]: readOnly,
                })}
            >
                {this.getPreviewText() || placeholder || ''}
            </div>
        );
    };

    render() {
        const {
            options,
            fullWidth,
            label,
            value,
            placeholder,
            error,
            readOnly,
            className,
            previewButtonFill,
            enabled,
            longOptionList,
            upper,
            containerRef,
            customTooltipText,
            customTooltipClassName,
            optionsListHeight,
            errorClassName,
            name,
            labelClassName: labelExternalClass,
            errorAbsolutePosition,
            anchorId,
            extraHeight = false,
            required,
            isNoteAfterInput,
            register,
        } = this.props;

        const { isOpen } = this.state;

        const wrapperClassName = cn(styles.selectWrapper, className, {
            [styles.fullWidth]: fullWidth,
            [styles.disabled]: !enabled,
        });
        const labelClassName = cn(styles.previewLabel, labelExternalClass, {
            [styles.primaryLabel]: isOpen,
            [styles.filledLabel]: !isOpen && !!value,
            [styles.errorLabel]: !isOpen && Boolean(error),
        });
        const previewClassName = cn(styles.preview, this.props.previewClassName, {
            [styles.placeholder]: !value && !!placeholder,
            [styles.previewOpen]: isOpen,
            [styles.errorInput]: error,
        });
        const previewButtonClassName = cn(styles.previewButton, { [styles.opened]: isOpen });

        const filteredOptions = options.filter(this.filterOptions);

        return (
            <OutsideClickHandler onOutsideClick={() => this.closeList()}>
                <div className={wrapperClassName} ref={containerRef} id={anchorId}>
                    <div className={styles.previewContainer}>
                        {register && (
                            <Input register={register} name={name} required={required} withoutClearBtn hidden />
                        )}
                        {label && <div className={labelClassName}>{label}</div>}
                        <div
                            className={previewClassName}
                            onClick={this.toggleList}
                            onMouseEnter={() => this.setState({ tooltipHovered: true })}
                            onMouseLeave={() => this.setState({ tooltipHovered: false })}
                            id={`${name}_preview`}
                        >
                            {this.renderPreview()}
                            {!readOnly && (
                                <ExpandIcon
                                    className={cn(previewButtonClassName, styles.expandIcon)}
                                    fill={previewButtonFill}
                                />
                            )}
                        </div>
                        <div className={styles.errorAndListContainer}>
                            {error && (
                                <div
                                    className={cn(
                                        styles.error,
                                        errorAbsolutePosition && styles.absolute,
                                        errorClassName,
                                    )}
                                >
                                    {error}
                                </div>
                            )}
                            {this.state.isOpen && (
                                <div
                                    className={cn(
                                        styles.optionsList,
                                        upper && styles.upper,
                                        extraHeight && styles.extraHeight,
                                    )}
                                    style={{ maxHeight: longOptionList ? '13rem' : optionsListHeight }}
                                >
                                    <Scrollbars
                                        autoHeight
                                        autoHeightMax={longOptionList ? '13rem' : optionsListHeight}
                                        ref={this.scrollbarsRef}
                                    >
                                        {this.renderOptions(filteredOptions)}
                                    </Scrollbars>
                                </div>
                            )}
                            {customTooltipText && (
                                <CustomTooltip
                                    text={customTooltipText}
                                    displayed={
                                        this.state.tooltipHovered &&
                                        !this.state.isOpen &&
                                        !error &&
                                        !this.state.isChanged
                                    }
                                    className={customTooltipClassName}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </OutsideClickHandler>
        );
    }

    getPreviewText() {
        const { multiple, value, options, t, name } = this.props;

        if (!multiple) {
            return this.getValueLabel();
        }

        return value
            .map((v) => {
                const option = _.find(options, { value: v });
                if (!option) {
                    return '';
                }

                return translationFields.includes(name) ? t(option.label) : option.label;
            })
            .join(', ');
    }

    getValueLabel() {
        let { value } = this.props;
        const { options, name, t } = this.props;

        if (_.isObject(value)) {
            value = value.value;
        }

        const item = options.find((i) => _.isObject(i) && i.value === value);

        if (item) {
            return translationFields.includes(name) ? t(item.label) : item.label;
        }

        return translationFields.includes(name) ? t(value) : value;
    }
}

const WithTranslation = withTranslation()(Select);

export default forwardRef((props, ref) => <WithTranslation {...props} containerRef={ref} />);
